import { useEffect } from "react";
import { usePlayer } from "../state/player";
import { nativeRuntime } from "../platform/native";
import { currentAudibleItem, type AudibleItem } from "./useAudioSession";

export function mediaPosition(audible: AudibleItem | null): MediaPositionState | null {
  if (!audible || !(audible.itemDurationUs > 0)) return null;
  const duration = audible.itemDurationUs / 1_000_000;
  return {
    duration,
    position: Math.min(Math.max(0, audible.itemPositionUs / 1_000_000), duration),
    playbackRate: 1,
  };
}

export interface MediaSessionInput {
  audio: React.RefObject<HTMLAudioElement | null>;
  title: string;
  artist: string | null;
  artworkUrl: string | null;
  toggle: () => void;
  step: (direction: -1 | 1) => void;
  seek: (seconds: number) => void;
  nudgeSeconds: number;
}

export function useMediaSession({
  audio,
  title,
  artist,
  artworkUrl,
  toggle,
  step,
  seek,
  nudgeSeconds,
}: MediaSessionInput): void {
  const idle = usePlayer((state) => state.source === null);
  const playing = usePlayer((state) => state.playing);
  const positionMs = usePlayer((state) => state.positionMs);
  const browser = nativeRuntime() === null;

  useEffect(() => {
    if (!browser) return;
    if (!("mediaSession" in navigator)) return;
    const session = navigator.mediaSession;
    const handlers: [MediaSessionAction, MediaSessionActionHandler][] = [
      [
        "play",
        () => {
          if (audio.current?.paused !== false) toggle();
        },
      ],
      [
        "pause",
        () => {
          if (audio.current?.paused === false) toggle();
        },
      ],
      ["previoustrack", () => void step(-1)],
      ["nexttrack", () => void step(1)],
      ["seekbackward", (details) => seek(-(details.seekOffset ?? nudgeSeconds))],
      ["seekforward", (details) => seek(details.seekOffset ?? nudgeSeconds)],
      [
        "seekto",
        (details) => {
          const audible = currentAudibleItem();
          if (!audible || details.seekTime === undefined || !Number.isFinite(details.seekTime))
            return;
          const itemPositionUs = Math.min(
            Math.max(0, Math.round(details.seekTime * 1_000_000)),
            audible.itemDurationUs,
          );
          usePlayer.getState().requestSeek({
            kind: "media",
            mediaUs: audible.mediaUs - audible.itemPositionUs + itemPositionUs,
          });
        },
      ],
    ];
    for (const [action, handler] of handlers) {
      try {
        session.setActionHandler(action, handler);
      } catch {
        // Unsupported Media Session actions are optional.
      }
    }
    return () => {
      for (const [action] of handlers) {
        try {
          session.setActionHandler(action, null);
        } catch {
          // The action was never registered.
        }
      }
    };
  }, [audio, browser, nudgeSeconds, seek, step, toggle]);

  useEffect(() => {
    if (!browser) return;
    if (!("mediaSession" in navigator)) return;
    if (idle) {
      navigator.mediaSession.metadata = null;
      return;
    }
    navigator.mediaSession.metadata = new MediaMetadata({
      title,
      artist: artist ?? "",
      artwork: artworkUrl ? [{ src: artworkUrl, type: "image/jpeg" }] : [],
    });
  }, [artist, artworkUrl, browser, idle, title]);

  useEffect(() => {
    if (!browser) return;
    if (!("mediaSession" in navigator)) return;
    const session = navigator.mediaSession;
    session.playbackState = idle ? "none" : playing ? "playing" : "paused";
    const state = mediaPosition(currentAudibleItem());
    if (state) session.setPositionState?.(state);
    else session.setPositionState?.();
  }, [browser, idle, playing, positionMs]);
}
